import axios from 'axios';
import { aadConfig, getAccessToken } from '../aad';
import type {
  PolicyPack,
  PolicyPackActivation,
  PolicyPackResponse,
  PolicyPackSummary,
  PolicyPackType,
  PolicyPackVersionInfo,
  PolicyRule,
} from '../dto/policyPack';
import apiProxy from './apiProxy';

/*
 * The Rulebook's API: policy packs on the AI host, same client and token as every other AI Hub call.
 *
 * Every route names the account, so the host's account guard refuses a caller who is not on it, and
 * the service then answers 404 for anything that is not in that account. The whole API sits behind
 * the FEATURE_POLICY_PACKS switch and answers 404 while it is off, which is how the settings nav
 * decides whether to show the Rulebook at all.
 */

const base = (accountId: string) => `${process.env.AI_HOST}/v1/accounts/${accountId}/policy-packs`;

export const listPolicyPacks = apiProxy(
  'listPolicyPacks',
  async (accountId: string, type?: PolicyPackType): Promise<PolicyPackSummary[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${base(accountId)}${type ? `?type=${type}` : ''}`);
    return data;
  },
);

/** One version, by number or "active". */
export const getPolicyPackVersion = apiProxy(
  'getPolicyPackVersion',
  async (accountId: string, packKey: string, version: number | 'active'): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}`);
    return data;
  },
);

export const listPolicyPackVersions = apiProxy(
  'listPolicyPackVersions',
  async (accountId: string, packKey: string): Promise<PolicyPackVersionInfo[]> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.get(`${base(accountId)}/${encodeURIComponent(packKey)}/versions`);
    return data;
  },
);

/** A new pack: from scratch, or a copy of a platform template. */
export const createPolicyPack = apiProxy(
  'createPolicyPack',
  async (
    accountId: string,
    body:
      | { fromTemplate: string }
      | { packKey: string; packType: PolicyPackType; label: string; domainProfile: 'building' | 'transportation' },
  ): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(base(accountId), body);
    return data;
  },
);

/** A new draft copied from the live version. Editing a live pack always starts here. */
export const createPolicyPackDraft = apiProxy(
  'createPolicyPackDraft',
  async (accountId: string, packKey: string): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(`${base(accountId)}/${encodeURIComponent(packKey)}/drafts`);
    return data;
  },
);

/** Replaces a draft's content. The server answers 409 for anything that is not a draft. */
export const savePolicyPackDraft = apiProxy(
  'savePolicyPackDraft',
  async (accountId: string, pack: PolicyPack): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.put(
      `${base(accountId)}/${encodeURIComponent(pack.packKey)}/versions/${pack.version}`,
      pack,
    );
    return data;
  },
);

/** Puts a draft into force. Owners and administrators of the account only; the server enforces it. */
export const activatePolicyPack = apiProxy(
  'activatePolicyPack',
  async (
    accountId: string,
    packKey: string,
    version: number,
    activation: PolicyPackActivation,
  ): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}/activate`,
      activation,
    );
    return data;
  },
);

/**
 * Uploads a policy document to a draft. It lands in the account's private policy-sources store and
 * is recorded on the draft's source documents.
 */
export const uploadPolicySource = apiProxy(
  'uploadPolicySource',
  async (
    accountId: string,
    packKey: string,
    version: number,
    file: File,
    meta: { title: string; edition?: string; docId?: string },
  ): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const body = new FormData();
    body.append('file', file);
    body.append('title', meta.title);
    if (meta.edition) body.append('edition', meta.edition);
    if (meta.docId) body.append('docId', meta.docId);
    const { data } = await axios.post(
      `${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}/sources`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },
);

/**
 * Discards a draft. The server refuses anything that is not a draft (409) and anyone who is not an
 * owner or administrator (403). packRemoved is true when it was the pack's only version.
 */
export const discardPolicyPackDraft = apiProxy(
  'discardPolicyPackDraft',
  async (accountId: string, packKey: string, version: number): Promise<{ packRemoved: boolean }> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.delete(`${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}`);
    return data;
  },
);

/** Reads a document again for proposed rules: after a failure, or a second time. */
export const readPolicySource = apiProxy(
  'readPolicySource',
  async (accountId: string, packKey: string, version: number, docId: string): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}/sources/${encodeURIComponent(docId)}/read`,
    );
    return data;
  },
);

/** Accept, reject or put back to pending; or send the edited rule, which accepts it as edited. */
export const decidePolicyProposal = apiProxy(
  'decidePolicyProposal',
  async (
    accountId: string,
    packKey: string,
    version: number,
    proposalId: string,
    decision: { status: 'accepted' | 'rejected' | 'pending' } | { rule: PolicyRule },
  ): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}/proposals/${encodeURIComponent(proposalId)}`,
      decision,
    );
    return data;
  },
);

/** Moves every accepted proposal into the draft's rules. The draft still needs approval. */
export const addAcceptedPolicyProposals = apiProxy(
  'addAcceptedPolicyProposals',
  async (accountId: string, packKey: string, version: number): Promise<PolicyPackResponse> => {
    await getAccessToken(aadConfig().accountScopes);
    const { data } = await axios.post(
      `${base(accountId)}/${encodeURIComponent(packKey)}/versions/${version}/add-accepted-proposals`,
    );
    return data;
  },
);

/** The server's own words from a failed call ("Only an administrator of this account..."), when it sent any. */
export const policyPackErrorMessage = (err: unknown): string | undefined => {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
  return typeof data?.message === 'string' && data.message ? data.message : undefined;
};

/** Whether a failed call was a 404: the pack is not there, or the whole feature is switched off. */
export const isPolicyPackNotFound = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 404;
